// Starting regions — chosen at new-game (Satisfactory-style), they give each
// playthrough its own economy/difficulty flavour. Modelled exactly like
// difficultyConfig.ts: a Record of multipliers + a getXMods() that reads the
// store. CRITICAL INVARIANT: 'costa_sol' is the IDENTITY region (every
// multiplier 1, every delta 0) and is the store default, so the deterministic
// tests (which run through resetGame() → region 'costa_sol') stay green. Region
// modifiers MULTIPLY alongside the difficulty modifiers; they never replace them.

import { useGameStore } from '@/store/gameStore'

export type RegionId =
  | 'costa_sol'
  | 'capital'
  | 'pueblo_interior'
  | 'isla_turistica'
  | 'norte_lluvioso'
  | 'resort_lujo'

export type WeatherProfile = 'mild' | 'sunny' | 'rainy' | 'arid'

export interface RegionModifiers {
  // Compose multiplicatively with the difficulty modifiers
  incomeMultiplier: number
  expenseMultiplier: number
  demandMultiplier: number
  // Applied once, only in the new-game flow
  startingMoneyMultiplier: number
  startingReputationDelta: number
  // Stored for later phases (not read by any system yet)
  rivalStrengthDelta: number
  weatherProfile: WeatherProfile
  touristSeasonality: number
}

export const REGION_CONFIG: Record<RegionId, RegionModifiers> = {
  // IDENTITY — never give this anything but 1 / 0 (tests depend on it)
  costa_sol: {
    incomeMultiplier: 1, expenseMultiplier: 1, demandMultiplier: 1,
    startingMoneyMultiplier: 1, startingReputationDelta: 0,
    rivalStrengthDelta: 0, weatherProfile: 'mild', touristSeasonality: 1,
  },
  capital: {
    incomeMultiplier: 1.15, expenseMultiplier: 1.25, demandMultiplier: 1.2,
    startingMoneyMultiplier: 1, startingReputationDelta: 0,
    rivalStrengthDelta: 8, weatherProfile: 'mild', touristSeasonality: 1,
  },
  pueblo_interior: {
    incomeMultiplier: 0.9, expenseMultiplier: 0.7, demandMultiplier: 0.85,
    startingMoneyMultiplier: 1.2, startingReputationDelta: 0.3,
    rivalStrengthDelta: -8, weatherProfile: 'sunny', touristSeasonality: 0.8,
  },
  isla_turistica: {
    incomeMultiplier: 1.1, expenseMultiplier: 1.1, demandMultiplier: 1,
    startingMoneyMultiplier: 0.9, startingReputationDelta: 0,
    rivalStrengthDelta: 0, weatherProfile: 'sunny', touristSeasonality: 1.6,
  },
  norte_lluvioso: {
    incomeMultiplier: 0.95, expenseMultiplier: 0.95, demandMultiplier: 0.95,
    startingMoneyMultiplier: 1.1, startingReputationDelta: 0,
    rivalStrengthDelta: -2, weatherProfile: 'rainy', touristSeasonality: 0.9,
  },
  resort_lujo: {
    incomeMultiplier: 1.3, expenseMultiplier: 1.45, demandMultiplier: 0.95,
    startingMoneyMultiplier: 0.85, startingReputationDelta: 0.2,
    rivalStrengthDelta: 4, weatherProfile: 'arid', touristSeasonality: 1.1,
  },
}

export type RegionDifficultyHint = 'Fácil' | 'Normal' | 'Media' | 'Difícil'

export interface RegionMeta {
  label: string
  /** Short lore line on the selection card */
  tagline: string
  /** Welcome paragraph shown in the intro */
  intro: string
  heroImage: string
  /** Two-stop gradient used as a placeholder until the art exists / on load error */
  gradient: [string, string]
  difficultyHint: RegionDifficultyHint
}

export const REGION_META: Record<RegionId, RegionMeta> = {
  costa_sol: {
    label: 'Costa del Sol',
    tagline: 'La cuna del pádel: sol, turismo y afición todo el año.',
    intro: 'Bienvenido a la Costa del Sol, donde el pádel nunca descansa. Sol casi todo el año, turistas con ganas de jugar y una afición fiel. El equilibrio perfecto para levantar un club: ni regalado, ni imposible. Tu historia empieza aquí.',
    heroImage: '/assets/regions/costa_sol.webp',
    gradient: ['#1b2c3f', '#e0894a'],
    difficultyHint: 'Normal',
  },
  capital: {
    label: 'Gran Capital',
    tagline: 'Mucho público y dinero, pero el alquiler y los rivales no perdonan.',
    intro: 'La gran ciudad: miles de jugadores potenciales y dinero en el aire, pero el alquiler dispara los gastos y los clubes rivales son fieras. Si triunfas aquí, triunfas en cualquier sitio. ¿Estás a la altura de la capital?',
    heroImage: '/assets/regions/capital.webp',
    gradient: ['#141a2e', '#3b5bdb'],
    difficultyHint: 'Difícil',
  },
  pueblo_interior: {
    label: 'Pueblo del Interior',
    tagline: 'Todo barato y rivales flojos, pero hay poca gente.',
    intro: 'Un pueblo tranquilo del interior. El terreno y los gastos son baratísimos y los rivales no aprietan, pero hay poca gente y la demanda es modesta. Un buen sitio para aprender el oficio sin agobios. Construye despacio y bien.',
    heroImage: '/assets/regions/pueblo_interior.webp',
    gradient: ['#1e2a1c', '#9bbf57'],
    difficultyHint: 'Fácil',
  },
  isla_turistica: {
    label: 'Isla Turística',
    tagline: 'Veranos que revientan, inviernos que vacían las pistas.',
    intro: 'Una isla que vive del turismo: en verano las pistas no dan abasto, pero en invierno el club se queda casi vacío. Gestionar esos vaivenes de temporada será tu gran reto. ¿Sabrás aguantar la temporada baja para reventar en la alta?',
    heroImage: '/assets/regions/isla_turistica.webp',
    gradient: ['#10293a', '#2bb5c9'],
    difficultyHint: 'Media',
  },
  norte_lluvioso: {
    label: 'Norte Lluvioso',
    tagline: 'La lluvia castiga el exterior; aquí el indoor es el rey.',
    intro: 'El norte verde y lluvioso. Las pistas exteriores sufren la lluvia constante, así que el pádel indoor es prácticamente obligatorio. La afición es entregada y los gastos moderados, pero el clima manda. Cubre tus pistas o paga las consecuencias.',
    heroImage: '/assets/regions/norte_lluvioso.webp',
    gradient: ['#16242a', '#5a8f8f'],
    difficultyHint: 'Media',
  },
  resort_lujo: {
    label: 'Resort de Lujo',
    tagline: 'Clientela rica y precios altos, pero mantenerlo cuesta una fortuna.',
    intro: 'Un resort exclusivo en pleno desierto costero. Clientela adinerada dispuesta a pagar precios altos, pero mantener unas instalaciones de lujo cuesta una auténtica fortuna y empiezas con poco margen. Alto riesgo, alta recompensa. Solo para valientes.',
    heroImage: '/assets/regions/resort_lujo.webp',
    gradient: ['#2a1f16', '#d9a441'],
    difficultyHint: 'Difícil',
  },
}

export const DEFAULT_REGION: RegionId = 'costa_sol'

/** Ordered for the selection grid (default/easiest first) */
export const REGION_ORDER: RegionId[] = [
  'costa_sol', 'pueblo_interior', 'isla_turistica', 'norte_lluvioso', 'capital', 'resort_lujo',
]

/** Modifiers for the region currently set in the store (default = identity) */
export function getRegionMods(): RegionModifiers {
  const r = useGameStore.getState().region ?? DEFAULT_REGION
  return REGION_CONFIG[r]
}

export interface RegionTrait {
  kind: 'pro' | 'con' | 'neutral'
  label: string
}

// Only surface deviations of >= 10% so the pills stay meaningful (sub-10%
// wobble like norte's 0.95s isn't worth a pill).
const TRAIT_THRESHOLD = 0.1

/**
 * Player-facing pros/cons for a region's selection card — DERIVED from the
 * modifiers (never hand-written) so a balance tweak can't desync the copy.
 * Shows the trade-off: up to 2 pros + 1-2 cons (always at least one con when
 * one exists), magnitude-ranked. The identity region returns a neutral note.
 */
export function getRegionTraits(id: RegionId): RegionTrait[] {
  const m = REGION_CONFIG[id]
  const pros: { label: string; w: number }[] = []
  const cons: { label: string; w: number }[] = []

  if (m.startingMoneyMultiplier >= 1 + TRAIT_THRESHOLD) pros.push({ label: 'Más capital inicial', w: m.startingMoneyMultiplier - 1 })
  else if (m.startingMoneyMultiplier <= 1 - TRAIT_THRESHOLD) cons.push({ label: 'Menos capital inicial', w: 1 - m.startingMoneyMultiplier })

  if (m.demandMultiplier >= 1 + TRAIT_THRESHOLD) pros.push({ label: 'Mucha clientela', w: m.demandMultiplier - 1 })
  else if (m.demandMultiplier <= 1 - TRAIT_THRESHOLD) cons.push({ label: 'Poca clientela', w: 1 - m.demandMultiplier })

  if (m.incomeMultiplier >= 1 + TRAIT_THRESHOLD) pros.push({ label: 'Buenos ingresos', w: m.incomeMultiplier - 1 })
  else if (m.incomeMultiplier <= 1 - TRAIT_THRESHOLD) cons.push({ label: 'Ingresos ajustados', w: 1 - m.incomeMultiplier })

  // For costs, the LOW multiplier is the advantage
  if (m.expenseMultiplier <= 1 - TRAIT_THRESHOLD) pros.push({ label: 'Costes bajos', w: 1 - m.expenseMultiplier })
  else if (m.expenseMultiplier >= 1 + TRAIT_THRESHOLD) cons.push({ label: 'Costes altos', w: m.expenseMultiplier - 1 })

  pros.sort((a, b) => b.w - a.w)
  cons.sort((a, b) => b.w - a.w)

  const topPros = pros.slice(0, 2)
  const topCons = cons.slice(0, topPros.length >= 2 ? 1 : 2)

  const out: RegionTrait[] = [
    ...topPros.map(p => ({ kind: 'pro' as const, label: p.label })),
    ...topCons.map(c => ({ kind: 'con' as const, label: c.label })),
  ]
  if (out.length === 0) out.push({ kind: 'neutral', label: 'Equilibrada, sin sorpresas' })
  return out
}
